import {Component, computed, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {NgClass} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {driver, DriveStep, Side} from 'driver.js';
import {CountdownPipe, LocalizedDatePipe, NoContentComponent, PageSelectorComponent, TooltipDirective} from '@/app/shared';
import {BattlePepsService} from '@services/battlepeps.service';
import {AuthService} from '@services/auth.service';
import {ToastService} from '@services/toast.service';
import {environment} from '@/environments/environment';
import {BattlePepsCurrent} from '@/app/core/models/battlepeps/battlePepsCurrent';
import {BattlePepsTierProgress} from '@/app/core/models/battlepeps/battlePepsTierProgress';
import {RankTier} from '@/app/core/models/battlepeps/rankTier';
import {BattlePepsLeaderboardEntry} from '@/app/core/models/battlepeps/battlePepsLeaderboardEntry';
import {BattlePepsQuestForPlayer} from '@/app/core/models/battlepeps/battlePepsQuestForPlayer';
import {CompletedQuest} from '@/app/core/models/battlepeps/completedQuest';
import {QuestScope} from '@/app/core/models/battlepeps/questScope';
import {rankIcon} from '@/app/core/utils/rankUtils';
import {GlobalQuestService} from '@services/global-quest.service';
import {GlobalQuest} from '@/app/core/models/battlepeps/globalQuest';

const SCOPE_LABEL_KEYS: Record<QuestScope, string> = {
	ONE_TIME: 'one-time',
	REPEATABLE: 'repeatable',
	DAILY: 'daily',
	PER_STREAM: 'per-stream',
	MONTHLY: 'monthly',
};

@Component({
	selector: 'app-battlepeps',
	imports: [
		TranslatePipe,
		NgClass,
		NoContentComponent,
		PageSelectorComponent,
		CountdownPipe,
		LocalizedDatePipe,
		TooltipDirective,
		RouterLink,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './battlepeps.component.html',
})
export class BattlePepsComponent implements OnInit {

	private readonly battlePepsService = inject(BattlePepsService);
	protected readonly authService = inject(AuthService);
	private readonly toastService = inject(ToastService);
	private readonly titleService = inject(Title);
	private readonly translate = inject(TranslateService);
	private readonly router = inject(Router);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly globalQuestService = inject(GlobalQuestService);

	/** Invite Discord — même serveur que le lien du footer. */
	private readonly DISCORD_INVITE_URL = 'https://discord.gg/T92qhMUyRV';

	/** Chaîne à suivre pour la quête FOLLOW_TWITCH (fefegg, distincte du channel des lives officiels). */
	private readonly TWITCH_CHANNEL_URL = 'https://www.twitch.tv/fefegg';

	/** Chaîne YouTube de fefegg — quête SUB_YOUTUBE. */
	private readonly YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/channel/UCcrtW1kHXYDdIAAMkzeXFbg';

	/** Contraintes client quêtes requiresImage — revalidées côté serveur de toute façon. */
	private static readonly MAX_QUEST_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;
	private static readonly ALLOWED_QUEST_IMAGE_TYPES = ['image/jpeg', 'image/png'];

	/** Tutoriel driver.js : lancé une seule fois automatiquement, rejouable via le bouton "?". */
	private static readonly TUTORIAL_SEEN_KEY = 'battlepeps-tutorial-seen';

	isLoading = signal(true);
	noSeason = signal(false);

	current: WritableSignal<BattlePepsCurrent | null> = signal(null);
	tiers: WritableSignal<BattlePepsTierProgress[]> = signal([]);
	ranks: WritableSignal<RankTier[]> = signal([]);
	quests: WritableSignal<BattlePepsQuestForPlayer[]> = signal([]);
	completedQuests: WritableSignal<CompletedQuest[]> = signal([]);
	globalQuests: WritableSignal<GlobalQuest[]> = signal([]);
	activeQuest = signal(0);

	leaderboard: WritableSignal<BattlePepsLeaderboardEntry[]> = signal([]);
	leaderboardPage = signal(0);
	leaderboardTotalPages = signal(0);

	claimingTier: WritableSignal<number | null> = signal(null);
	claimingAll = signal(false);
	/** Numéros de palier venant d'être récupérés : déclenche le flash doré autour de la récompense. */
	justClaimedTiers: WritableSignal<Set<number>> = signal(new Set());
	declaringQuestId: WritableSignal<string | null> = signal(null);
	verifyingQuestId: WritableSignal<string | null> = signal(null);
	protected isLeaderboardOpen = signal<boolean>(false);

	pendingRewards = computed(() => this.tiers().filter(t => t.status === 'PENDING_CLAIM'));
	hasPendingRewards = computed(() => this.pendingRewards().length > 0);
	sortedTiers = computed(() => [...this.tiers()].sort((a, b) => a.tierNumber - b.tierNumber));

	currentUsername = computed(() => this.authService.getUser()?.username ?? null);

	/**
	 * À réaliser : jamais déclarée, rejetée, ou validée-mais-rejouable (une
	 * REPEATABLE ou DAILY validée reste affichée et refaisable — à l'infini
	 * pour REPEATABLE, une fois par jour pour DAILY, le backend rejette la
	 * tentative si c'est trop tôt ; une ONE_TIME validée ou rejetée sort de
	 * cette liste — rejetée reste bloquée, validée bascule dans l'historique).
	 */
	todoQuests = computed(() => this.quests().filter(q =>
		q.completionStatus === null
		|| q.completionStatus === 'REJECTED'
		|| (q.completionStatus === 'VALIDATED' && q.scope !== 'ONE_TIME')
	));
	/** En attente de validation admin : ni "à faire" ni "faite". */
	pendingQuests = computed(() => this.quests().filter(q => q.completionStatus === 'PENDING_VALIDATION'));

	/** Progression XP au sein du palier courant (le seuil de palier est fixe : season.xpPerTier). */
	tierProgressPercent = computed(() => {
		const current = this.current();
		const progress = current?.progress;
		if (!current || !progress) return 0;
		const xpPerTier = current.season.xpPerTier;
		const {xpCurrent, xpForNextTier} = progress;
		if (xpPerTier <= 0) return 0;
		const tierStart = xpForNextTier - xpPerTier;
		const percent = ((xpCurrent - tierStart) / xpPerTier) * 100;
		return Math.min(100, Math.max(0, percent));
	});

	/** Progression XP entre le rang courant et le suivant (100 si rang max déjà atteint). */
	rankProgressPercent = computed(() => {
		const progress = this.current()?.progress;
		if (!progress?.currentRank) return 0;
		const next = progress.nextRank;
		if (!next) return 100;
		const span = next.thresholdXp - progress.currentRank.thresholdXp;
		if (span <= 0) return 100;
		const percent = ((progress.xpCurrent - progress.currentRank.thresholdXp) / span) * 100;
		return Math.min(100, Math.max(0, percent));
	});

	rankIcon(code: string): string {
		return rankIcon(code);
	}

	rankLabel(code: string | null): string {
		if (!code) return '';
		return this.ranks().find(r => r.code === code)?.label ?? code;
	}

	questScopeLabelKey(scope: QuestScope): string {
		return 'page.battlepeps.quests.scope.' + SCOPE_LABEL_KEYS[scope];
	}

	/** EN si la traduction existe et que la langue courante est EN, sinon FR — cf. quest.titleEn/descriptionEn (backend). */
	questTitle(quest: {title: string; titleEn: string | null}): string {
		return this.translate.currentLang === 'en' && quest.titleEn ? quest.titleEn : quest.title;
	}

	questDescription(quest: {description: string | null; descriptionEn: string | null}): string | null {
		return this.translate.currentLang === 'en' && quest.descriptionEn ? quest.descriptionEn : quest.description;
	}

	/** Nom du rôle Tipeee, EN si dispo et langue EN, sinon FR — cf. quête « rôle Discord » (TIP_ROLE). */
	tipRoleTierLabel(tier: {label: string; labelEn: string | null}): string {
		return this.translate.currentLang === 'en' && tier.labelEn ? tier.labelEn : tier.label;
	}

	loginWithTwitch(): void {
		window.location.href = `${environment.backendUrl}/auth/twitch/authorize?origin=main`;
	}

	ngOnInit(): void {
		this.titleService.setTitle('Team Peps - ' + this.translate.instant('page.battlepeps.title'));
		this.loadCurrent();
		this.handleYoutubeReturn();
	}

	/**
	 * Retour du flux OAuth one-shot SUB_YOUTUBE : lit ?questVerified / ?questError,
	 * affiche un toast, rafraîchit et nettoie l'URL.
	 */
	private handleYoutubeReturn(): void {
		const params = this.activatedRoute.snapshot.queryParamMap;
		const verified = params.get('questVerified');
		const error = params.get('questError');
		if (!verified && !error) return;

		if (verified === 'SUB_YOUTUBE') {
			this.toastService.show(this.translate.instant('page.battlepeps.quests.verify-youtube-success'), 'success', 5000);
			if (this.authService.isAuthenticated()) {
				this.loadQuests();
				this.loadCompletedQuests();
				this.refreshProgress();
				this.loadTiers();
			}
		} else if (error === 'not-subscribed') {
			this.toastService.show(this.translate.instant('page.battlepeps.quests.verify-not-fulfilled-sub-youtube'), 'error', 6000);
			window.open(this.YOUTUBE_CHANNEL_URL, '_blank');
		} else if (error === 'denied') {
			this.toastService.show(this.translate.instant('page.battlepeps.quests.verify-youtube-denied'), 'error', 5000);
		} else if (error === 'already-done') {
			if (this.authService.isAuthenticated()) this.loadQuests();
		} else {
			this.toastService.show(this.translate.instant('page.battlepeps.quests.verify-youtube-failed'), 'error', 5000);
		}

		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams: {questVerified: null, questError: null},
			queryParamsHandling: 'merge',
			replaceUrl: true,
		});
	}

	onQuestScroll(slider: HTMLElement): void {
		this.activeQuest.set(Math.round(slider.scrollLeft / slider.clientWidth));
	}

	prevQuest(slider: HTMLElement): void {
		this.goToQuest(slider, Math.max(0, this.activeQuest() - 1));
	}

	nextQuest(slider: HTMLElement): void {
		this.goToQuest(slider, Math.min(this.globalQuests().length - 1, this.activeQuest() + 1));
	}

	goToQuest(slider: HTMLElement, index: number): void {
		slider.scrollTo({left: index * slider.clientWidth, behavior: 'smooth'});
	}

	private loadGlobalQuests(): void {
		this.globalQuestService.getCurrent().subscribe({
			next: (quests) => this.globalQuests.set(quests),
			error: () => {}
		});
	}

	private loadCurrent(): void {
		this.isLoading.set(true);
		this.battlePepsService.getCurrent().subscribe({
			next: (data) => {
				this.current.set(data);
				this.noSeason.set(false);
				this.isLoading.set(false);
				this.loadRanks();
				this.loadGlobalQuests();
				this.loadLeaderboard(data.season.id, 0);
				// Paliers et quêtes n'ont pas de sens sans compte (403 en anonyme côté API).
				if (this.authService.isAuthenticated()) {
					this.loadTiers(true);
					this.loadQuests();
					this.loadCompletedQuests();
				}
				this.maybeAutoStartTour();
			},
			error: (err: HttpErrorResponse) => {
				this.isLoading.set(false);
				if (err.status === 404) {
					this.noSeason.set(true);
				} else {
					this.toastService.show(this.translate.instant('page.battlepeps.errors.load'), 'error', 5000);
				}
			}
		});
	}

	/**
	 * `scrollToCurrent` : uniquement au premier chargement, pour centrer le track
	 * comme un battle pass. Sur un refetch (après un claim), on ne déplace pas la
	 * vue — l'utilisateur reste sur le palier qu'il regardait.
	 */
	private loadTiers(scrollToCurrent = false): void {
		this.battlePepsService.getCurrentTiers().subscribe({
			next: (tiers) => {
				this.tiers.set(tiers);
				if (scrollToCurrent) this.scrollToCurrentTier();
			},
			error: () => {}
		});
	}

	/** Centre le track sur le palier courant au chargement, comme un battle pass (Overwatch...). */
	private scrollToCurrentTier(): void {
		const currentTier = this.current()?.progress?.currentTier;
		if (currentTier == null) return;
		setTimeout(() => {
			const tier = document.getElementById(`tier-${currentTier}`);
			const track = tier?.closest<HTMLElement>('.overflow-x-auto');
			if (!tier || !track) return;
			const offset = tier.getBoundingClientRect().left - track.getBoundingClientRect().left;
			const left = track.scrollLeft + offset - (track.clientWidth - tier.offsetWidth) / 2;
			track.scrollTo({left: Math.max(0, left), behavior: 'smooth'});
		}, 100);
	}

	private loadRanks(): void {
		this.battlePepsService.getRanks().subscribe({
			next: (ranks) => this.ranks.set([...ranks].sort((a, b) => a.displayOrder - b.displayOrder)),
			error: () => {}
		});
	}

	private loadQuests(): void {
		this.battlePepsService.getCurrentQuests().subscribe({
			next: (quests) => this.quests.set(quests),
			error: () => {}
		});
	}

	private loadCompletedQuests(): void {
		this.battlePepsService.getCompletedQuests().subscribe({
			next: (completed) => this.completedQuests.set(completed),
			error: () => {}
		});
	}

	loadLeaderboard(battlePepsId: string, page: number): void {
		this.battlePepsService.getLeaderboard(battlePepsId, page).subscribe({
			next: (data) => {
				this.leaderboard.set(data.content);
				this.leaderboardPage.set(data.number);
				this.leaderboardTotalPages.set(data.totalPages);
			},
			error: () => {}
		});
	}

	onLeaderboardPageChange(page: number): void {
		const id = this.current()?.season.id;
		if (!id) return;
		this.loadLeaderboard(id, page);
	}

/** Après un claim réussi : refetch current + paliers (coins/boosters déjà crédités côté serveur), et rafraîchit le solde global affiché ailleurs (navbar, shop...). */
	private refreshAfterClaim(): void {
		this.loadTiers();
		this.battlePepsService.getCurrent().subscribe({
			next: (data) => this.current.set(data),
			error: () => {}
		});
		this.authService.loadUser().subscribe({error: () => {}});
	}

	claimTier(tier: BattlePepsTierProgress): void {
		if (tier.status !== 'PENDING_CLAIM' || this.claimingTier() !== null || this.claimingAll()) return;
		this.claimingTier.set(tier.tierNumber);
		this.battlePepsService.claimTier(tier.tierNumber).subscribe({
			next: () => {
				this.claimingTier.set(null);
				this.toastService.show(this.translate.instant('page.battlepeps.rewards.claim-success'), 'success', 4000);
				this.flashClaimedTiers([tier.tierNumber]);
				this.refreshAfterClaim();
			},
			error: (err: HttpErrorResponse) => {
				this.claimingTier.set(null);
				this.showApiError(err, 'page.battlepeps.rewards.claim-error');
				this.loadTiers();
			}
		});
	}

	claimAll(): void {
		if (this.claimingAll() || this.claimingTier() !== null || !this.hasPendingRewards()) return;
		const claimedTierNumbers = this.pendingRewards().map(t => t.tierNumber);
		this.claimingAll.set(true);
		this.battlePepsService.claimAll().subscribe({
			next: (claimed) => {
				this.claimingAll.set(false);
				this.toastService.show(
					this.translate.instant('page.battlepeps.rewards.claim-all-success', {count: claimed.length}),
					'success', 4000
				);
				this.flashClaimedTiers(claimedTierNumbers);
				this.refreshAfterClaim();
			},
			error: (err: HttpErrorResponse) => {
				this.claimingAll.set(false);
				this.showApiError(err, 'page.battlepeps.rewards.claim-error');
				this.loadTiers();
			}
		});
	}

	/** Déclenche le flash doré autour des récompenses récupérées, le temps de l'animation. */
	private flashClaimedTiers(tierNumbers: number[]): void {
		this.justClaimedTiers.set(new Set(tierNumbers));
		setTimeout(() => this.justClaimedTiers.set(new Set()), 1400);
	}

	canDeclare(quest: BattlePepsQuestForPlayer): boolean {
		if (quest.verificationMethod !== 'MANUAL_ADMIN') return false;
		if (quest.completionStatus === null) return true;
		// Rejetée ou déjà validée : redéclarable seulement si REPEATABLE/DAILY — une ONE_TIME rejetée/validée reste bloquée.
		if (quest.completionStatus === 'REJECTED' || quest.completionStatus === 'VALIDATED') return quest.scope !== 'ONE_TIME';
		return false;
	}

	declareQuest(quest: BattlePepsQuestForPlayer): void {
		if (this.declaringQuestId() !== null || !this.canDeclare(quest)) return;
		this.declaringQuestId.set(quest.id);
		this.battlePepsService.declareQuest(quest.id).subscribe({
			next: (res) => {
				this.declaringQuestId.set(null);
				this.toastService.show(res.message, 'success', 4000);
				this.loadQuests();
			},
			error: (err: HttpErrorResponse) => {
				this.declaringQuestId.set(null);
				this.showApiError(err, 'page.battlepeps.quests.declare-error');
				this.loadQuests();
			}
		});
	}

	/** Quêtes requiresImage (BUY_PEPS_BUNDLE, COMMANDE_HOLY, COMMANDE_BRIGADE...) : preuve envoyée directement à Discord pour review (pas de stockage chez nous). */
	declareQuestWithImage(quest: BattlePepsQuestForPlayer, input: HTMLInputElement): void {
		if (this.declaringQuestId() !== null || !this.canDeclare(quest)) return;
		const file = input.files?.[0];
		if (!file) return;

		if (!BattlePepsComponent.ALLOWED_QUEST_IMAGE_TYPES.includes(file.type)) {
			this.toastService.show(this.translate.instant('page.battlepeps.quests.image-invalid-type'), 'error', 6000);
			input.value = '';
			return;
		}
		if (file.size > BattlePepsComponent.MAX_QUEST_IMAGE_SIZE_BYTES) {
			this.toastService.show(this.translate.instant('page.battlepeps.quests.image-too-large'), 'error', 6000);
			input.value = '';
			return;
		}

		this.declaringQuestId.set(quest.id);
		this.battlePepsService.declareQuestWithImage(quest.id, file).subscribe({
			next: (res) => {
				this.declaringQuestId.set(null);
				input.value = '';
				this.toastService.show(res.message, 'success', 4000);
				this.loadQuests();
			},
			error: (err: HttpErrorResponse) => {
				this.declaringQuestId.set(null);
				input.value = '';
				this.showApiError(err, 'page.battlepeps.quests.declare-error');
				this.loadQuests();
			}
		});
	}

	canVerify(quest: BattlePepsQuestForPlayer): boolean {
		if (quest.verificationMethod !== 'EXTERNAL_API') return false;
		if (quest.completionStatus === null) return true;
		// Refusée ou déjà validée : re-vérifiable seulement si REPEATABLE/DAILY — une ONE_TIME refusée/validée reste bloquée.
		if (quest.completionStatus === 'REJECTED' || quest.completionStatus === 'VALIDATED') return quest.scope !== 'ONE_TIME';
		return false;
	}

	verifyQuest(quest: BattlePepsQuestForPlayer): void {
		if (this.verifyingQuestId() !== null || !this.canVerify(quest)) return;

		// SUB_YOUTUBE : flux OAuth Google one-shot (pas de liaison de compte).
		// On quitte le site vers Google ; le retour se fait sur cette page avec
		// ?questVerified / ?questError, traité dans ngOnInit.
		if (quest.templateCode === 'SUB_YOUTUBE') {
			this.verifyingQuestId.set(quest.id);
			this.battlePepsService.getYoutubeAuthorizeUrl(quest.id).subscribe({
				next: (res) => { window.location.href = res.url; },
				error: (err: HttpErrorResponse) => {
					this.verifyingQuestId.set(null);
					this.showApiError(err, 'page.battlepeps.quests.verify-youtube-failed');
				}
			});
			return;
		}

		this.verifyingQuestId.set(quest.id);
		this.battlePepsService.verifyQuest(quest.id).subscribe({
			next: (res) => {
				this.verifyingQuestId.set(null);
				this.toastService.show(res.message, 'success', 4000);
				this.loadQuests();
				this.loadCompletedQuests();
				this.refreshProgress();
				// L'XP gagnée peut débloquer des paliers : refetch sans scroll (loadTiers(false)).
				this.loadTiers();
			},
			error: (err: HttpErrorResponse) => {
				this.verifyingQuestId.set(null);
				this.handleVerifyError(err, quest);
				this.loadQuests();
			}
		});
	}

	/** Distingue les 400 "métier" (Discord non lié / condition pas encore remplie) des autres erreurs. */
	private handleVerifyError(err: HttpErrorResponse, quest: BattlePepsQuestForPlayer): void {
		const message: string = err?.error?.message ?? '';
		if (err.status === 400 && message.includes('compte Discord')) {
			this.toastService.show(message || this.translate.instant('page.battlepeps.quests.verify-discord-required'), 'error', 6000);
			this.router.navigate(['/profil']);
			return;
		}
		if (err.status === 400 && message.includes("n'est pas encore remplie")) {
			// TIP_ROLE : la condition manquante est le rôle Discord (attribué par
			// Tipeee), pas la présence sur le serveur — message et action dédiés,
			// distincts du cas générique "rejoindre le Discord" (JOIN_DISCORD).
			if (quest.templateCode === 'TIP_ROLE') {
				this.toastService.show(this.translate.instant('page.battlepeps.quests.verify-not-fulfilled-tip-role'), 'error', 6000);
				return;
			}
			if (quest.templateCode === 'FOLLOW_TWITCH') {
				this.toastService.show(this.translate.instant('page.battlepeps.quests.verify-not-fulfilled-follow-twitch'), 'error', 6000);
				window.open(this.TWITCH_CHANNEL_URL, '_blank');
				return;
			}
			this.toastService.show(this.translate.instant('page.battlepeps.quests.verify-not-fulfilled'), 'error', 6000);
			// Seule la quête JOIN_DISCORD attend une action sur Discord (rejoindre le
			// serveur) : pour les autres templates, ouvrir l'invite n'a pas de sens.
			if (quest.templateCode === 'JOIN_DISCORD') {
				window.open(this.DISCORD_INVITE_URL, '_blank');
			}
			return;
		}
		this.showApiError(err, 'page.battlepeps.quests.verify-error');
	}

	/** Rafraîchit uniquement la progression XP (palier courant), sans toucher aux paliers/récompenses. */
	private refreshProgress(): void {
		this.battlePepsService.getCurrent().subscribe({
			next: (data) => this.current.set(data),
			error: () => {}
		});
	}

	private showApiError(err: HttpErrorResponse, fallbackKey: string): void {
		const message = (err as HttpErrorResponse)?.error?.message;
		this.toastService.show(message || this.translate.instant(fallbackKey), 'error', 5000);
	}

	/**
	 * Première visite uniquement (flag localStorage) : laisse le temps au
	 * @defer et aux sections dépendantes de l'auth (paliers, quêtes...) de
	 * s'afficher avant de lancer le tour — driver.js ignore de toute façon
	 * les étapes dont l'élément est absent (skipMissingElement).
	 */
	private maybeAutoStartTour(): void {
		// `localStorage` n'existe pas côté serveur (SSR) : ngOnInit s'exécute
		// aussi pendant le rendu serveur, sans ce garde-fou l'accès y lève une
		// ReferenceError et fait échouer le rendu de la page.
		try {
			if (localStorage.getItem(BattlePepsComponent.TUTORIAL_SEEN_KEY)) return;
		} catch {
			return;
		}
		setTimeout(() => this.startTour(), 800);
	}

	/** Rejoue le tutoriel — bouton "?" de l'en-tête. */
	startTour(): void {
		const steps = this.buildTourSteps();
		if (steps.length === 0) return;
		localStorage.setItem(BattlePepsComponent.TUTORIAL_SEEN_KEY, 'true');
		driver({
			showProgress: true,
			smoothScroll: true,
			overlayColor: 'rgba(19,20,21,0.75)',
			popoverClass: 'peps-driver-popover',
			progressText: '{{current}} / {{total}}',
			nextBtnText: this.translate.instant('page.battlepeps.tutorial.next'),
			prevBtnText: this.translate.instant('page.battlepeps.tutorial.prev'),
			doneBtnText: this.translate.instant('page.battlepeps.tutorial.done'),
			steps,
		}).drive();
	}

	private buildTourSteps(): DriveStep[] {
		const step = (selector: string, key: string, side: Side = 'bottom'): DriveStep => ({
			element: selector,
			skipMissingElement: true,
			popover: {
				side,
				align: 'start',
				title: this.translate.instant(`page.battlepeps.tutorial.${key}.title`),
				description: this.translate.instant(`page.battlepeps.tutorial.${key}.text`),
			},
		});

		const steps: DriveStep[] = [
			step('[data-tour="header"]', 'header'),
		];

		if (this.current()?.progress) {
			steps.push(
				step('[data-tour="xp-progress"]', 'xp'),
				step('[data-tour="rank"]', 'rank'),
				step('[data-tour="rewards"]', 'rewards', 'top'),
			);
		} else {
			steps.push(step('[data-tour="login-cta"]', 'login'));
		}

		steps.push(step('[data-tour="leaderboard"]', 'leaderboard', 'top'));

		if (this.authService.isAuthenticated()) {
			steps.push(step('[data-tour="quests"]', 'quests', 'top'));
		}

		return steps;
	}

	protected toggleLeaderboard() {
		this.isLeaderboardOpen.set(!this.isLeaderboardOpen());
	}
}
